/**
 * useInitialUserData hook module.
 * Provides a hook to fetch and store initial user data (account, subscription, billing) after authentication and session load.
 */

// External packages
import { useEffect, useRef } from "react";

// Internal shared modules
import { useAuthContext } from "../../../context/AuthContext";
import { useAccountContext } from "../../../context/AccountContext";
import { useSubscriptionContext } from "../../../context/SubscriptionContext";

// Relative imports - API
import { getAccountLight } from "../../../api/selfcare/getAccountLightApi";
import { getSubscription } from "../../../api/selfcare/getSubscriptionApi";
import { getBillingData } from "../../../api/selfcare/getBillingDataApi";

// Relative imports - Registry
import { inFlightSubs } from "../../registry/subscriptionFetchRegistry";

/**
 * Custom React hook to fetch and store initial user data after authentication and session loader completion.
 * Pulls account, subscription, and billing data exactly once per session.
 *
 * Side effects: Updates user context with fetched data and manages loading state.
 */
export default function useInitialUserData() {
  const ran = useRef(false);
  const { isLoggedIn, isSessionLoading, loginLevel, userType, setIsSessionLoading } =
    useAuthContext();
  const { storeAccountData } = useAccountContext();
  const { storeSubscriptions, storeSubscriptionData, storeBillingSingle } =
    useSubscriptionContext();

  /**
   * Helper to fetch data or return null on error.
   * @param {Function} fn - API function to call.
   * @param {any} args - Arguments for the API function.
   * @returns {Promise<any|null>} The data or null if error.
   */
  const fetchOrNull = async (fn, args) => {
    try {
      const res = await fn(args);
      return res?.status === 1 && res.data != null ? res.data : null;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    if (!isLoggedIn || isSessionLoading || ran.current) return;

    (async () => {
      ran.current = true;

      // First, fetch the lightweight account data which contains the subscription list.
      // This is required before fetching other data.
      const acc = await fetchOrNull(getAccountLight);
      if (!acc) return setIsSessionLoading(false);

      const { subscriptions, ...accountFields } = acc;
      storeAccountData(accountFields);
      storeSubscriptions(subscriptions ?? []);

      const firstSub = acc.subscriptions?.[0];
      if (!firstSub) return setIsSessionLoading(false);

      // Second, for account owners (loginLevel 0) or representatives, fetch full subscription details
      // if they aren't already included. Use the in-flight registry to prevent race conditions.
      if (
        (loginLevel === 0 || userType === "representative") &&
        !firstSub.all_data
      ) {
        inFlightSubs.add(firstSub.id);
        try {
          const sub = await fetchOrNull(getSubscription, { id: firstSub.id });
          if (sub) storeSubscriptionData(sub);
        } finally {
          inFlightSubs.delete(firstSub.id);
        }
      } else if (firstSub.all_data) {
        storeSubscriptionData(firstSub);
      }

      // Finally, fetch the billing data for the primary subscription.
      const bill = await fetchOrNull(getBillingData, { ids: [firstSub.id] });
      if (bill) storeBillingSingle(firstSub.id, bill);

      setIsSessionLoading(false);
    })();
  }, [
    isLoggedIn,
    isSessionLoading,
    loginLevel,
    userType,
    storeAccountData,
    storeSubscriptions,
    storeSubscriptionData,
    storeBillingSingle,
    setIsSessionLoading,
  ]);
}
